import React, { useEffect, useState, useContext } from 'react';
import api from '../api/axios';
import { AuthContext } from '../AuthContext';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  Avatar, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress, 
  Alert,
  Divider,
  IconButton,
  MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import LaunchIcon from '@mui/icons-material/Launch';
import ColorLensIcon from '@mui/icons-material/ColorLens';

const CompanySelectionPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [industry, setIndustry] = useState('');
  // Plan state removed
  const [primaryColor, setPrimaryColor] = useState('#667eea');
  const [actionColor, setActionColor] = useState('#667eea');
  const [useMonochrome, setUseMonochrome] = useState(true);
  const [logo, setLogo] = useState('');

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing', 
    'Retail', 'Consulting', 'Real Estate', 'Legal', 'Non-Profit', 'Other'
  ];

  // Plans functionality removed as requested

  const fetchCompanies = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/companies');
      setCompanies(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err.response?.status === 404) {
        // Endpoint not implemented yet, show empty state
        console.log('Companies endpoint not yet implemented, showing empty state');
        setCompanies([]);
      } else {
        setError('Failed to load companies');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const resetForm = () => {
    setName('');
    setDescription('');
    setIndustry('');
    setPrimaryColor('#667eea');
    setActionColor('#667eea');
    setUseMonochrome(true);
    setLogo('');
  };

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const companyData = {
        name,
        description,
        industry,
        branding: {
          primaryColor,
          actionColor: useMonochrome ? primaryColor : actionColor,
          logo
        }
      };
      
      console.log('Creating company:', companyData);
      const response = await api.post('/companies', companyData);
      const newCompany = {
        ...response.data,
        role: 'owner', // Owner created it
        primaryColor: response.data.settings?.theme?.primaryColor || primaryColor,
        branding: response.data.branding || companyData.branding
      };
      
      setCompanies(prev => [...prev, newCompany]);
      setCreateDialogOpen(false);
      resetForm();
      
      // Navigate to the new company's dashboard
      setTimeout(() => {
        navigate(`/company/${newCompany._id}`);
      }, 500);
      
    } catch (err) {
      console.error('Create company error:', err);
      setError(err.response?.data?.message || 'Failed to create company');
    }
    setCreating(false);
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setName(company.name);
    setDescription(company.description || '');
    setIndustry(company.industry || '');
    // Plan field removed
    setPrimaryColor(company.primaryColor || '#667eea');
    setActionColor(company.branding?.actionColor || company.primaryColor || '#667eea');
    setUseMonochrome(!company.branding?.actionColor || company.branding?.actionColor === company.primaryColor);
    setLogo(company.branding?.logo || '');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setError('');
    try {
      const updateData = {
        name,
        description,
        industry,
        branding: {
          primaryColor,
          actionColor: useMonochrome ? primaryColor : actionColor
        }
      };
      
      console.log('Updating company:', editingCompany._id, updateData);
      const response = await api.put(`/companies/${editingCompany._id}`, updateData);
      
      const updatedCompany = {
        ...response.data,
        role: editingCompany.role, // Preserve role
        primaryColor: response.data.settings?.theme?.primaryColor || primaryColor,
        branding: response.data.branding || updateData.branding
      };
      
      setCompanies(prev => prev.map(c => 
        c._id === editingCompany._id ? updatedCompany : c
      ));
      setEditDialogOpen(false);
      resetForm();
      setEditingCompany(null);
    } catch (err) {
      console.error('Update company error:', err);
      setError(err.response?.data?.message || 'Failed to update company');
    }
    setSaving(false);
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('Delete this company? This will also delete all associated projects. This action cannot be undone.')) return;
    
    try {
      console.log('Deleting company:', companyId);
      await api.delete(`/companies/${companyId}`);
      setCompanies(prev => prev.filter(c => c._id !== companyId));
    } catch (err) {
      console.error('Delete company error:', err);
      setError(err.response?.data?.message || 'Failed to delete company');
    }
  };

  // getPlanInfo function removed

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'warning';
      case 'editor': return 'info';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box maxWidth={1200} mx="auto" mt={4} px={2}>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        bgcolor: '#f5f5f5',
        minHeight: '100vh',
        width: '100%',
        '& .MuiCard-root': {
          bgcolor: '#ffffff !important',
        },
        '& .MuiTableContainer-root': {
          bgcolor: '#ffffff !important',
        }
      }}
    >
      <Box 
        maxWidth={1200} 
        mx="auto" 
        mt={4} 
        px={3}
        py={3}
      >
      {/* Header */}
      <Paper elevation={1} sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
          <Box>
            <Typography variant="h4" fontWeight="bold" mb={1}>
              Your Companies
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Manage your clients and organizations. Each company can have multiple projects and custom branding.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              minWidth: { xs: 'auto', md: 'inherit' },
              px: { xs: 2, md: 3 },
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)'
              }
            }}
          >
            Create Company
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Companies Grid */}
      <Grid container spacing={3}>
        {companies.map((company) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={company._id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                {/* Company Header */}
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar 
                    sx={{ 
                      bgcolor: company.primaryColor || '#667eea', 
                      width: 48, 
                      height: 48 
                    }}
                  >
                    <BusinessIcon />
                  </Avatar>
                  <Box flexGrow={1}>
                    <Typography variant="h6" fontWeight="bold" noWrap>
                      {company.name}
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <Chip 
                        label={company.role} 
                        color={getRoleColor(company.role)}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <IconButton 
                    size="small" 
                    onClick={() => handleEdit(company)}
                    disabled={company.role === 'viewer'}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Company Details */}
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, minHeight: '2.5em' }}>
                  {company.description || 'No description provided'}
                </Typography>

                {company.industry && (
                  <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                    Industry: {company.industry}
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Stats */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <AccountTreeIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="textSecondary">
                      {company.projectCount || 0} projects
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    Created {new Date(company.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>

              <CardActions>
                <Button 
                  size="small" 
                  startIcon={<LaunchIcon />}
                  onClick={() => navigate(`/company/${company._id}`)}
                  fullWidth
                  variant="contained"
                  sx={{ bgcolor: company.primaryColor || '#667eea' }}
                >
                  Open Dashboard
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}

        {/* Empty State */}
        {companies.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 6, 
                textAlign: 'center', 
                border: '2px dashed', 
                borderColor: 'grey.300',
                bgcolor: 'grey.50'
              }}
            >
              <BusinessIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" mb={1}>
                No companies yet
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={3}>
                Create your first company to start organizing your data extraction projects
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create Your First Company
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create Company Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon />
            Create New Company
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} pt={2}>
            <TextField
              label="Company Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              select
              label="Industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              fullWidth
            >
              {industries.map((ind) => (
                <MenuItem key={ind} value={ind}>
                  {ind}
                </MenuItem>
              ))}
            </TextField>
            <Box>
              <Typography variant="subtitle2" mb={1} display="flex" alignItems="center" gap={1}>
                <ColorLensIcon fontSize="small" />
                Brand Colors
              </Typography>
              
              {/* Primary Color */}
              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <TextField
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  sx={{ width: 80 }}
                />
                <TextField
                  label="Primary Color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  fullWidth
                  helperText="Headers, navigation, company identity"
                />
              </Box>
              
              {/* Action Color */}
              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <TextField
                  type="color"
                  value={useMonochrome ? primaryColor : actionColor}
                  onChange={(e) => {
                    setActionColor(e.target.value);
                    setUseMonochrome(false);
                  }}
                  disabled={useMonochrome}
                  sx={{ width: 80 }}
                />
                <TextField
                  label="Action Button Color"
                  value={useMonochrome ? primaryColor : actionColor}
                  onChange={(e) => {
                    setActionColor(e.target.value);
                    setUseMonochrome(false);
                  }}
                  disabled={useMonochrome}
                  fullWidth
                  helperText="Start buttons, important CTAs"
                />
              </Box>
              
              {/* Monochrome Checkbox */}
              <Box display="flex" alignItems="center">
                <input
                  type="checkbox"
                  id="use-monochrome-create"
                  checked={useMonochrome}
                  onChange={(e) => setUseMonochrome(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <label htmlFor="use-monochrome-create" style={{ cursor: 'pointer', fontSize: '0.875rem' }}>
                  Use primary color for all actions (recommended)
                </label>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreate} 
            variant="contained" 
            disabled={creating || !name.trim()}
            startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {creating ? 'Creating...' : 'Create Company'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon />
            Edit Company
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} pt={2}>
            <TextField
              label="Company Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              select
              label="Industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              fullWidth
            >
              {industries.map((ind) => (
                <MenuItem key={ind} value={ind}>
                  {ind}
                </MenuItem>
              ))}
            </TextField>
            <Box>
              <Typography variant="subtitle2" mb={1} display="flex" alignItems="center" gap={1}>
                <ColorLensIcon fontSize="small" />
                Brand Colors
              </Typography>
              
              {/* Primary Color */}
              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <TextField
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  sx={{ width: 80 }}
                />
                <TextField
                  label="Primary Color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  fullWidth
                  helperText="Headers, navigation, company identity"
                />
              </Box>
              
              {/* Action Color */}
              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <TextField
                  type="color"
                  value={useMonochrome ? primaryColor : actionColor}
                  onChange={(e) => {
                    setActionColor(e.target.value);
                    setUseMonochrome(false);
                  }}
                  disabled={useMonochrome}
                  sx={{ width: 80 }}
                />
                <TextField
                  label="Action Button Color"
                  value={useMonochrome ? primaryColor : actionColor}
                  onChange={(e) => {
                    setActionColor(e.target.value);
                    setUseMonochrome(false);
                  }}
                  disabled={useMonochrome}
                  fullWidth
                  helperText="Start buttons, important CTAs"
                />
              </Box>
              
              {/* Monochrome Checkbox */}
              <Box display="flex" alignItems="center">
                <input
                  type="checkbox"
                  id="use-monochrome-edit"
                  checked={useMonochrome}
                  onChange={(e) => setUseMonochrome(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <label htmlFor="use-monochrome-edit" style={{ cursor: 'pointer', fontSize: '0.875rem' }}>
                  Use primary color for all actions (recommended)
                </label>
              </Box>
            </Box>
            {editingCompany?.role === 'owner' && (
              <Box display="flex" justifyContent="flex-end">
                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    setEditDialogOpen(false);
                    handleDelete(editingCompany._id);
                  }}
                >
                  Delete Company
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            disabled={saving || !name.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default CompanySelectionPage; 